/**
 * Smart Recommendation Service
 * 
 * Analyzes user interactions in real-time and generates contextual recommendations:
 * - Persona mode switches (e.g., suggest Educator when user is struggling)
 * - Token/upgrade prompts (low balance, heavy usage patterns)
 * - Depth adjustments (suggest concise for simple questions, detailed for complex)
 * - Quiz suggestions (repeated topic questions → suggest quiz)
 * - Mock exam readiness (high quiz scores → suggest mock exam)
 * - Study tips (long sessions, repeated errors)
 */

export type RecommendationType =
    | 'switch_persona'
    | 'upgrade_account'
    | 'low_tokens'
    | 'adjust_depth'
    | 'take_quiz'
    | 'take_mock_exam'
    | 'take_break'
    | 'try_attachment'
    | 'enable_premium_model'
    | 'flashcard_suggestion'
    | 'review_errors'
    | 'try_study_guide'
    | 'computation_practice'
    | 'explore_standards'
    | 'save_session';

export interface SmartRecommendation {
    id: string;
    type: RecommendationType;
    title: string;
    description: string;
    icon: string; // emoji
    primaryAction: {
        label: string;
        action: string; // action key to handle in App.tsx
    };
    secondaryAction?: {
        label: string;
        action: string;
    };
    priority: 'low' | 'medium' | 'high';
    expiresAt?: number; // auto-dismiss timestamp
    dismissable: boolean;
}

interface InteractionSignal {
    timestamp: number;
    type: 'message' | 'feedback' | 'quiz_complete' | 'error';
    data: Record<string, any>;
}

interface TopicTracker {
    topic: string;
    messageCount: number;
    lastSeen: number;
    feedbackScores: ('positive' | 'negative' | 'neutral')[];
}

class SmartRecommendationService {
    private static instance: SmartRecommendationService;

    // Interaction history for analysis
    private recentSignals: InteractionSignal[] = [];
    private topicTracker: Map<string, TopicTracker> = new Map();
    private dismissedRecommendations: Set<string> = new Set();
    private lastRecommendationTime: Map<RecommendationType, number> = new Map();
    private sessionMessageCount: number = 0;
    private sessionStartTime: number = Date.now();
    private consecutiveNegativeFeedback: number = 0;
    private lastPersonaSuggestionDismissed: number = 0;

    // Cooldowns (ms) — prevent spamming the same recommendation type
    private readonly COOLDOWNS: Record<RecommendationType, number> = {
        switch_persona: 10 * 60 * 1000,     // 10 minutes
        upgrade_account: 30 * 60 * 1000,     // 30 minutes
        low_tokens: 15 * 60 * 1000,          // 15 minutes
        adjust_depth: 15 * 60 * 1000,        // 15 minutes
        take_quiz: 20 * 60 * 1000,           // 20 minutes
        take_mock_exam: 60 * 60 * 1000,      // 1 hour
        take_break: 45 * 60 * 1000,          // 45 minutes
        try_attachment: 30 * 60 * 1000,      // 30 minutes
        enable_premium_model: 20 * 60 * 1000, // 20 minutes
        flashcard_suggestion: 25 * 60 * 1000, // 25 minutes
        review_errors: 30 * 60 * 1000,       // 30 minutes
        try_study_guide: 40 * 60 * 1000,     // 40 minutes
        computation_practice: 20 * 60 * 1000, // 20 minutes
        explore_standards: 30 * 60 * 1000,   // 30 minutes
        save_session: 20 * 60 * 1000         // 20 minutes
    };

    private constructor() {}

    static getInstance(): SmartRecommendationService {
        if (!SmartRecommendationService.instance) {
            SmartRecommendationService.instance = new SmartRecommendationService();
        }
        return SmartRecommendationService.instance;
    }

    // ==================== Signal Recording ====================

    recordMessage(userQuery: string, aiResponse: string, domain?: string): void {
        this.sessionMessageCount++;
        this.recentSignals.push({
            timestamp: Date.now(),
            type: 'message',
            data: { userQuery, aiResponse, domain, queryLength: userQuery.length, responseLength: aiResponse.length }
        });

        // Track topics
        if (domain && domain !== 'GENERAL') {
            const tracker = this.topicTracker.get(domain) || {
                topic: domain,
                messageCount: 0,
                lastSeen: 0,
                feedbackScores: []
            };
            tracker.messageCount++;
            tracker.lastSeen = Date.now();
            this.topicTracker.set(domain, tracker);
        }

        // Keep only last 50 signals
        if (this.recentSignals.length > 50) {
            this.recentSignals = this.recentSignals.slice(-50);
        }
    }

    recordFeedback(rating: 'positive' | 'negative' | 'neutral', domain?: string): void {
        this.recentSignals.push({
            timestamp: Date.now(),
            type: 'feedback',
            data: { rating, domain }
        });

        if (rating === 'negative') {
            this.consecutiveNegativeFeedback++;
        } else if (rating === 'positive') {
            this.consecutiveNegativeFeedback = 0;
        }

        // Track per-topic feedback
        if (domain) {
            const tracker = this.topicTracker.get(domain);
            if (tracker) {
                tracker.feedbackScores.push(rating);
            }
        }
    }

    recordQuizComplete(score: number, total: number, topic: string): void {
        this.recentSignals.push({
            timestamp: Date.now(),
            type: 'quiz_complete',
            data: { score, total, percentage: Math.round((score / total) * 100), topic }
        });
    }

    recordError(errorType: string): void {
        this.recentSignals.push({
            timestamp: Date.now(),
            type: 'error',
            data: { errorType }
        });
    }

    dismissRecommendation(recommendationId: string): void {
        this.dismissedRecommendations.add(recommendationId);
        // Track persona suggestion dismissals specifically
        if (recommendationId.startsWith('switch_persona')) {
            this.lastPersonaSuggestionDismissed = Date.now();
        }
    }

    // ==================== Analysis Engine ====================

    /**
     * Main analysis method — called after each AI response.
     * Returns the highest-priority recommendation (if any).
     */
    analyze(context: {
        user: {
            tokenBalance?: number;
            isPremium?: boolean;
            isMax?: boolean;
            accountType?: string;
            totalQuestions?: number;
            activePromoEffects?: { unlimitedTokensUntil?: string };
        } | null;
        currentMode: 'standard' | 'educator' | 'lawyer';
        currentDepth: 'concise' | 'balanced' | 'detailed' | 'comprehensive';
        tokenLimit: number;
        hasUnlimitedTokens: boolean;
        lastQuery?: string;
        lastResponse?: string;
    }): SmartRecommendation | null {
        const candidates: SmartRecommendation[] = [];

        // Run all analyzers
        const personaRec = this.analyzePersonaSwitch(context);
        if (personaRec) candidates.push(personaRec);

        const tokenRec = this.analyzeTokenStatus(context);
        if (tokenRec) candidates.push(tokenRec);

        const depthRec = this.analyzeDepthAdjustment(context);
        if (depthRec) candidates.push(depthRec);

        const quizRec = this.analyzeQuizSuggestion(context);
        if (quizRec) candidates.push(quizRec);

        const examRec = this.analyzeExamReadiness(context);
        if (examRec) candidates.push(examRec);

        const breakRec = this.analyzeBreakSuggestion();
        if (breakRec) candidates.push(breakRec);

        const premiumModelRec = this.analyzePremiumModelSuggestion(context);
        if (premiumModelRec) candidates.push(premiumModelRec);

        const flashcardRec = this.analyzeFlashcardSuggestion(context);
        if (flashcardRec) candidates.push(flashcardRec);

        const errorReviewRec = this.analyzeErrorReview();
        if (errorReviewRec) candidates.push(errorReviewRec);

        const computationRec = this.analyzeComputationPractice(context);
        if (computationRec) candidates.push(computationRec);

        const standardsRec = this.analyzeStandardsExploration(context);
        if (standardsRec) candidates.push(standardsRec);

        const saveRec = this.analyzeSaveSession();
        if (saveRec) candidates.push(saveRec);

        const studyGuideRec = this.analyzeStudyGuideSuggestion(context);
        if (studyGuideRec) candidates.push(studyGuideRec);

        if (candidates.length === 0) return null;

        // Sort by priority (high > medium > low)
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        candidates.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

        const winner = candidates[0];

        // Record that we showed this type
        this.lastRecommendationTime.set(winner.type, Date.now());

        return winner;
    }

    // ==================== Individual Analyzers ====================

    private isOnCooldown(type: RecommendationType): boolean {
        const lastShown = this.lastRecommendationTime.get(type) || 0;
        return (Date.now() - lastShown) < this.COOLDOWNS[type];
    }

    /**
     * Suggest switching to Educator mode when user appears to be struggling
     */
    private analyzePersonaSwitch(context: {
        currentMode: string;
        lastQuery?: string;
        lastResponse?: string;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('switch_persona')) return null;
        if (context.currentMode === 'educator') return null;
        // Don't suggest again too soon after dismissal
        if (Date.now() - this.lastPersonaSuggestionDismissed < 20 * 60 * 1000) return null;

        // Trigger conditions:
        // 1. Multiple negative feedbacks in a row
        const hasNegativeStreak = this.consecutiveNegativeFeedback >= 2;

        // 2. User asking "explain again", "I don't understand", "what do you mean"
        const query = (context.lastQuery || '').toLowerCase();
        const isConfused = /i don'?t understand|explain again|what do you mean|can you simplify|too complicated|confused|hindi ko gets|hindi ko maintindihan|pa-?explain/i.test(query);

        // 3. Repeated questions on the same topic (asking similar things)
        const hasRepeatedTopic = Array.from(this.topicTracker.values()).some(
            t => t.messageCount >= 5 && t.feedbackScores.filter(f => f === 'negative').length >= 2
        );

        if (hasNegativeStreak || isConfused || hasRepeatedTopic) {
            const reason = isConfused
                ? "It looks like you'd like a clearer explanation."
                : hasNegativeStreak
                    ? "I noticed my recent answers weren't quite what you needed."
                    : "You've been working on this topic for a while.";

            return {
                id: `switch_persona_${Date.now()}`,
                type: 'switch_persona',
                title: 'Try Educator Mode?',
                description: `${reason} Educator mode breaks down concepts step-by-step with examples, making complex topics easier to grasp.`,
                icon: '🎓',
                primaryAction: { label: 'Switch to Educator', action: 'set_mode_educator' },
                secondaryAction: { label: 'Keep Current', action: 'dismiss' },
                priority: isConfused ? 'high' : 'medium',
                dismissable: true,
                expiresAt: Date.now() + 30000 // 30 seconds
            };
        }

        // Suggest Lawyer mode for legal queries
        if (context.currentMode !== 'lawyer') {
            const isLegalQuery = /contract|obligation|civil code|corporation|rcc|negotiable|agency|partnership law|article \d|g\.?r\.?\s*no|supreme court|irac|legal|liable|liability|damages|breach/i.test(query);
            const recentLegalCount = this.recentSignals.filter(
                s => s.type === 'message' && s.timestamp > Date.now() - 10 * 60 * 1000 &&
                    /contract|obligation|legal|liable|article|law|court|damages/i.test(s.data.userQuery || '')
            ).length;

            if (isLegalQuery && recentLegalCount >= 2) {
                return {
                    id: `switch_persona_lawyer_${Date.now()}`,
                    type: 'switch_persona',
                    title: 'Switch to Lawyer Mode?',
                    description: "You're asking legal questions. Lawyer mode provides IRAC-structured analysis with case citations and legal reasoning.",
                    icon: '⚖️',
                    primaryAction: { label: 'Switch to Lawyer', action: 'set_mode_lawyer' },
                    secondaryAction: { label: 'Keep Current', action: 'dismiss' },
                    priority: 'medium',
                    dismissable: true,
                    expiresAt: Date.now() + 30000
                };
            }
        }

        return null;
    }

    /**
     * Suggest upgrading when tokens are running low
     */
    private analyzeTokenStatus(context: {
        user: { tokenBalance?: number; isPremium?: boolean; isMax?: boolean; activePromoEffects?: { unlimitedTokensUntil?: string } } | null;
        tokenLimit: number;
        hasUnlimitedTokens: boolean;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('low_tokens')) return null;
        if (!context.user || context.hasUnlimitedTokens) return null;

        const balance = context.user.tokenBalance ?? 0;
        const limit = context.tokenLimit;
        const usagePercent = ((limit - balance) / limit) * 100;

        // Critical: < 10% remaining
        if (usagePercent >= 90 && balance > 0) {
            const isPremium = context.user.isPremium || context.user.isMax;
            return {
                id: `low_tokens_critical_${Date.now()}`,
                type: 'low_tokens',
                title: 'Tokens Almost Gone!',
                description: `You have ${balance.toLocaleString()} tokens left (${Math.round(100 - usagePercent)}% remaining). ${isPremium ? 'Get more tokens to keep learning.' : 'Upgrade to Premium for 5x more tokens and priority access.'}`,
                icon: '🪫',
                primaryAction: {
                    label: isPremium ? 'Get More Tokens' : 'Upgrade to Premium',
                    action: isPremium ? 'open_token_purchase' : 'open_upgrade_premium'
                },
                secondaryAction: { label: 'Later', action: 'dismiss' },
                priority: 'high',
                dismissable: true,
                expiresAt: Date.now() + 20000
            };
        }

        // Warning: < 25% remaining and heavy usage pattern
        if (usagePercent >= 75) {
            const recentMessageCount = this.recentSignals.filter(
                s => s.type === 'message' && s.timestamp > Date.now() - 30 * 60 * 1000
            ).length;

            // Only show if user is actively using (5+ messages in last 30 min)
            if (recentMessageCount >= 5) {
                return {
                    id: `low_tokens_warning_${Date.now()}`,
                    type: 'low_tokens',
                    title: 'Running Low on Tokens',
                    description: `You've used ${usagePercent.toFixed(0)}% of your tokens. At your current pace, you may run out soon. Top up to keep your study session going!`,
                    icon: '⚡',
                    primaryAction: { label: 'Top Up Tokens', action: 'open_token_purchase' },
                    secondaryAction: { label: 'Dismiss', action: 'dismiss' },
                    priority: 'medium',
                    dismissable: true,
                    expiresAt: Date.now() + 15000
                };
            }
        }

        // Upgrade suggestion for free users with moderate usage
        if (!context.user.isPremium && !context.user.isMax && this.sessionMessageCount >= 15) {
            if (!this.isOnCooldown('upgrade_account')) {
                this.lastRecommendationTime.set('upgrade_account', Date.now());
                return {
                    id: `upgrade_account_${Date.now()}`,
                    type: 'upgrade_account',
                    title: 'Unlock Premium Features',
                    description: "You're getting great use out of Luca! Premium gives you 5x more tokens, priority AI models, unlimited quizzes, and advanced study tools.",
                    icon: '👑',
                    primaryAction: { label: 'See Premium Benefits', action: 'open_upgrade_premium' },
                    secondaryAction: { label: 'Maybe Later', action: 'dismiss' },
                    priority: 'low',
                    dismissable: true,
                    expiresAt: Date.now() + 20000
                };
            }
        }

        return null;
    }

    /**
     * Suggest adjusting response depth based on usage patterns
     */
    private analyzeDepthAdjustment(context: {
        currentDepth: string;
        lastQuery?: string;
        lastResponse?: string;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('adjust_depth')) return null;

        const recentMessages = this.recentSignals.filter(
            s => s.type === 'message' && s.timestamp > Date.now() - 15 * 60 * 1000
        );
        if (recentMessages.length < 3) return null;

        // Detect short questions getting very long responses
        const avgQueryLen = recentMessages.reduce((sum, s) => sum + (s.data.queryLength || 0), 0) / recentMessages.length;
        const avgResponseLen = recentMessages.reduce((sum, s) => sum + (s.data.responseLength || 0), 0) / recentMessages.length;

        if (context.currentDepth !== 'concise' && avgQueryLen < 80 && avgResponseLen > 2000) {
            return {
                id: `adjust_depth_concise_${Date.now()}`,
                type: 'adjust_depth',
                title: 'Want Shorter Answers?',
                description: "Your questions are quick but my answers are quite long. Switch to Concise mode for brief, direct answers that save tokens.",
                icon: '⚡',
                primaryAction: { label: 'Switch to Concise', action: 'set_depth_concise' },
                secondaryAction: { label: 'Keep Detailed', action: 'dismiss' },
                priority: 'low',
                dismissable: true,
                expiresAt: Date.now() + 20000
            };
        }

        // Detect complex questions with concise mode
        if (context.currentDepth === 'concise' && avgQueryLen > 200) {
            return {
                id: `adjust_depth_detailed_${Date.now()}`,
                type: 'adjust_depth',
                title: 'Need More Detailed Answers?',
                description: "Your questions are quite detailed — you might benefit from switching to Balanced or Detailed mode for more thorough explanations.",
                icon: '📖',
                primaryAction: { label: 'Switch to Balanced', action: 'set_depth_balanced' },
                secondaryAction: { label: 'Keep Concise', action: 'dismiss' },
                priority: 'low',
                dismissable: true,
                expiresAt: Date.now() + 20000
            };
        }

        return null;
    }

    /**
     * Suggest taking a quiz when user has been studying a topic extensively
     */
    private analyzeQuizSuggestion(context: {
        user: { totalQuizzes?: number; [key: string]: any } | null;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('take_quiz')) return null;

        // Find topics with 6+ messages
        const hotTopics = Array.from(this.topicTracker.entries())
            .filter(([_, t]) => t.messageCount >= 6 && Date.now() - t.lastSeen < 15 * 60 * 1000)
            .sort((a, b) => b[1].messageCount - a[1].messageCount);

        if (hotTopics.length > 0) {
            const [topicKey, tracker] = hotTopics[0];
            const topicName = this.formatTopicName(topicKey);

            return {
                id: `take_quiz_${topicKey}_${Date.now()}`,
                type: 'take_quiz',
                title: `Test Your ${topicName} Knowledge?`,
                description: `You've been studying ${topicName} for a while. Take a quick quiz to test your understanding and identify gaps!`,
                icon: '📝',
                primaryAction: { label: 'Start Quiz', action: `start_quiz_${topicKey}` },
                secondaryAction: { label: 'Keep Studying', action: 'dismiss' },
                priority: 'medium',
                dismissable: true,
                expiresAt: Date.now() + 25000
            };
        }

        return null;
    }

    /**
     * Suggest mock exam when user has good quiz performance
     */
    private analyzeExamReadiness(context: {
        user: { totalQuizzes?: number; totalExams?: number; [key: string]: any } | null;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('take_mock_exam')) return null;

        const recentQuizzes = this.recentSignals.filter(
            s => s.type === 'quiz_complete' && s.timestamp > Date.now() - 60 * 60 * 1000
        );

        if (recentQuizzes.length >= 2) {
            const avgScore = recentQuizzes.reduce((sum, s) => sum + (s.data.percentage || 0), 0) / recentQuizzes.length;

            if (avgScore >= 75) {
                return {
                    id: `take_mock_exam_${Date.now()}`,
                    type: 'take_mock_exam',
                    title: 'Ready for a Mock Exam?',
                    description: `You scored an average of ${avgScore.toFixed(0)}% on recent quizzes — great work! Challenge yourself with a full mock board exam to simulate the real thing.`,
                    icon: '🏆',
                    primaryAction: { label: 'Start Mock Exam', action: 'open_mock_exam_config' },
                    secondaryAction: { label: 'Not Yet', action: 'dismiss' },
                    priority: 'medium',
                    dismissable: true,
                    expiresAt: Date.now() + 30000
                };
            }
        }

        return null;
    }

    /**
     * Suggest taking a break after long study sessions
     */
    private analyzeBreakSuggestion(): SmartRecommendation | null {
        if (this.isOnCooldown('take_break')) return null;

        const sessionDuration = Date.now() - this.sessionStartTime;
        const sessionMinutes = sessionDuration / (60 * 1000);

        // Suggest break after 90+ minutes of continuous use
        if (sessionMinutes >= 90 && this.sessionMessageCount >= 20) {
            return {
                id: `take_break_${Date.now()}`,
                type: 'take_break',
                title: 'Time for a Quick Break?',
                description: `You've been studying for ${Math.round(sessionMinutes)} minutes — impressive dedication! A short break can help you retain information better.`,
                icon: '☕',
                primaryAction: { label: 'Good Idea!', action: 'dismiss' },
                priority: 'low',
                dismissable: true,
                expiresAt: Date.now() + 15000
            };
        }

        return null;
    }

    /**
     * Suggest enabling premium model for complex questions
     */
    private analyzePremiumModelSuggestion(context: {
        user: { isPremium?: boolean; isMax?: boolean } | null;
        lastQuery?: string;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('enable_premium_model')) return null;
        if (!context.user?.isPremium && !context.user?.isMax) return null;

        // Check for complex computational questions with negative feedback
        const query = (context.lastQuery || '').toLowerCase();
        const isComplex = /consolidat|liquidat|variance|standard cost|deferred tax|business combination|proof of cash|installment|construction|percentage of completion/i.test(query);

        const recentNegative = this.recentSignals.filter(
            s => s.type === 'feedback' && s.data.rating === 'negative' && s.timestamp > Date.now() - 10 * 60 * 1000
        ).length;

        if (isComplex && recentNegative >= 1) {
            return {
                id: `enable_premium_model_${Date.now()}`,
                type: 'enable_premium_model',
                title: 'Use Advanced AI Model?',
                description: "This is a complex problem. Enable the Premium model for more accurate computations and deeper analysis.",
                icon: '🧠',
                primaryAction: { label: 'Enable Premium Model', action: 'enable_premium_model' },
                secondaryAction: { label: 'Keep Standard', action: 'dismiss' },
                priority: 'medium',
                dismissable: true,
                expiresAt: Date.now() + 20000
            };
        }

        return null;
    }

    // ==================== Additional Analyzers ====================

    /**
     * Suggest flashcards when user is studying definitions/concepts
     */
    private analyzeFlashcardSuggestion(context: {
        lastQuery?: string;
        lastResponse?: string;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('flashcard_suggestion')) return null;

        const recentDefinitionQueries = this.recentSignals.filter(
            s => s.type === 'message' && s.timestamp > Date.now() - 15 * 60 * 1000 &&
                /what is|define|meaning of|difference between|enumerate|types of|list/i.test(s.data.userQuery || '')
        ).length;

        if (recentDefinitionQueries >= 4) {
            return {
                id: `flashcard_suggestion_${Date.now()}`,
                type: 'flashcard_suggestion',
                title: 'Create Flashcards?',
                description: "You're learning lots of definitions and concepts. Generate flashcards to review them later with spaced repetition!",
                icon: '🃏',
                primaryAction: { label: 'Generate Flashcards', action: 'open_flashcard_generator' },
                secondaryAction: { label: 'Not Now', action: 'dismiss' },
                priority: 'low',
                dismissable: true,
                expiresAt: Date.now() + 20000
            };
        }

        return null;
    }

    /**
     * Suggest reviewing errors after quiz with low score
     */
    private analyzeErrorReview(): SmartRecommendation | null {
        if (this.isOnCooldown('review_errors')) return null;

        const recentQuizzes = this.recentSignals.filter(
            s => s.type === 'quiz_complete' && s.timestamp > Date.now() - 30 * 60 * 1000
        );

        const lowScoreQuiz = recentQuizzes.find(s => (s.data.percentage || 0) < 50);
        if (lowScoreQuiz) {
            const topic = lowScoreQuiz.data.topic || 'the topic';
            return {
                id: `review_errors_${Date.now()}`,
                type: 'review_errors',
                title: 'Review What You Missed?',
                description: `You scored ${lowScoreQuiz.data.percentage}% on ${topic}. Ask me to explain the concepts you got wrong — I'll break them down step by step.`,
                icon: '🔍',
                primaryAction: { label: 'Review Mistakes', action: `ask_review_${encodeURIComponent(topic)}` },
                secondaryAction: { label: 'Skip', action: 'dismiss' },
                priority: 'medium',
                dismissable: true,
                expiresAt: Date.now() + 25000
            };
        }

        return null;
    }

    /**
     * Suggest computation practice when user is asking theoretical questions about computational topics
     */
    private analyzeComputationPractice(context: {
        lastQuery?: string;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('computation_practice')) return null;

        // Detect theoretical questions about computational topics
        const computationalTopics = this.recentSignals.filter(
            s => s.type === 'message' && s.timestamp > Date.now() - 20 * 60 * 1000 &&
                /tax|depreciat|amortiz|interest|present value|annuity|break.?even|variance|budget|npv|irr|cost/i.test(s.data.userQuery || '') &&
                /what|explain|how|define|when/i.test(s.data.userQuery || '')
        ).length;

        if (computationalTopics >= 3) {
            return {
                id: `computation_practice_${Date.now()}`,
                type: 'computation_practice',
                title: 'Practice with Numbers?',
                description: "You've been learning computational concepts. Try a computation-focused quiz to practice applying the formulas!",
                icon: '🔢',
                primaryAction: { label: 'Computation Quiz', action: 'start_computation_quiz' },
                secondaryAction: { label: 'Keep Learning', action: 'dismiss' },
                priority: 'low',
                dismissable: true,
                expiresAt: Date.now() + 20000
            };
        }

        return null;
    }

    /**
     * Suggest exploring standards when user references them frequently
     */
    private analyzeStandardsExploration(context: {
        lastQuery?: string;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('explore_standards')) return null;

        const standardsReferences = this.recentSignals.filter(
            s => s.type === 'message' && s.timestamp > Date.now() - 20 * 60 * 1000 &&
                /pfrs\s*\d|pas\s*\d|nirc|section\s*\d|article\s*\d|ias\s*\d/i.test(s.data.userQuery || '')
        ).length;

        if (standardsReferences >= 3) {
            return {
                id: `explore_standards_${Date.now()}`,
                type: 'explore_standards',
                title: 'Browse Standards Library?',
                description: "You're referencing multiple standards. Open the Standards Browser for quick access to PFRS, PAS, and tax code provisions.",
                icon: '📚',
                primaryAction: { label: 'Open Standards', action: 'open_standards_browser' },
                secondaryAction: { label: 'Dismiss', action: 'dismiss' },
                priority: 'low',
                dismissable: true,
                expiresAt: Date.now() + 20000
            };
        }

        return null;
    }

    /**
     * Suggest saving/bookmarking the session when it's been productive
     */
    private analyzeSaveSession(): SmartRecommendation | null {
        if (this.isOnCooldown('save_session')) return null;

        // Productive session: 10+ messages with mostly positive feedback
        if (this.sessionMessageCount >= 10) {
            const feedbackSignals = this.recentSignals.filter(s => s.type === 'feedback');
            const positiveCount = feedbackSignals.filter(s => s.data.rating === 'positive').length;

            if (positiveCount >= 3) {
                return {
                    id: `save_session_${Date.now()}`,
                    type: 'save_session',
                    title: 'Great Study Session!',
                    description: `You've had ${this.sessionMessageCount} productive exchanges. Pin this session so you can easily find it later for review.`,
                    icon: '📌',
                    primaryAction: { label: 'Pin Session', action: 'pin_current_session' },
                    secondaryAction: { label: 'Skip', action: 'dismiss' },
                    priority: 'low',
                    dismissable: true,
                    expiresAt: Date.now() + 15000
                };
            }
        }

        return null;
    }

    /**
     * Suggest study guide generation for deep topic exploration
     */
    private analyzeStudyGuideSuggestion(context: {
        lastQuery?: string;
    }): SmartRecommendation | null {
        if (this.isOnCooldown('try_study_guide')) return null;

        // User has been asking many questions about the same domain
        const dominantTopic = Array.from(this.topicTracker.entries())
            .filter(([_, t]) => t.messageCount >= 8)
            .sort((a, b) => b[1].messageCount - a[1].messageCount)[0];

        if (dominantTopic) {
            const [topicKey, tracker] = dominantTopic;
            const topicName = this.formatTopicName(topicKey);

            return {
                id: `try_study_guide_${topicKey}_${Date.now()}`,
                type: 'try_study_guide',
                title: `Generate a ${topicName} Study Guide?`,
                description: `You've asked ${tracker.messageCount} questions about ${topicName}. I can create a comprehensive study guide covering all the key concepts.`,
                icon: '📋',
                primaryAction: { label: 'Generate Guide', action: `generate_study_guide_${topicKey}` },
                secondaryAction: { label: 'Not Now', action: 'dismiss' },
                priority: 'low',
                dismissable: true,
                expiresAt: Date.now() + 25000
            };
        }

        return null;
    }

    // ==================== Utilities ====================

    private formatTopicName(key: string): string {
        const names: Record<string, string> = {
            'TAX': 'Taxation',
            'AUD': 'Auditing',
            'FAR': 'Financial Accounting',
            'AFAR': 'Advanced Accounting',
            'MAS': 'Management Advisory',
            'RFBT': 'Business Law',
            'GENERAL': 'General'
        };
        return names[key] || key;
    }

    /**
     * Reset session tracking (call on new session)
     */
    resetSession(): void {
        this.sessionMessageCount = 0;
        this.sessionStartTime = Date.now();
        this.consecutiveNegativeFeedback = 0;
        this.topicTracker.clear();
        this.recentSignals = [];
    }

    /**
     * Get session stats for debugging
     */
    getStats() {
        return {
            sessionMessages: this.sessionMessageCount,
            sessionMinutes: Math.round((Date.now() - this.sessionStartTime) / 60000),
            trackedTopics: Array.from(this.topicTracker.entries()).map(([k, v]) => ({
                topic: k,
                messages: v.messageCount,
                feedback: v.feedbackScores
            })),
            consecutiveNegative: this.consecutiveNegativeFeedback,
            signalCount: this.recentSignals.length
        };
    }
}

export const smartRecommendationService = SmartRecommendationService.getInstance();
